import {
  FormLabel,
  FormControl,
  Autocomplete,
  ListItem,
  Checkbox,
  Chip,
  TextField,
  Typography,
} from "@mui/material";
import { Stack } from "@mui/system";
import { useTranslation } from "react-i18next";
import { AutocompleteFiltersData } from "../../core/models/supplies/supplies.model";
import { useCallback, useEffect, useMemo, useState } from "react";
import "./FilterCups.scss";
import {
  HistoricFilters,
  MasterFiltersManagementCups,
} from "../../core/models/managements/managements.model";

type ICupsFilters = Omit<HistoricFilters, "type" | "cityId">;

interface AutocompleteCupsFilters extends Omit<AutocompleteFiltersData, "id"> {
  id: keyof ICupsClientFilters;
}

interface FiltersCupsProps {
  filters: ICupsFilters;
  setFilters: React.Dispatch<React.SetStateAction<HistoricFilters>>;
  firstInputChanged: string;
  setFirstInputChanged: React.Dispatch<React.SetStateAction<string>>;
  masterFiltersCups: MasterFiltersManagementCups[];
}

interface ICupsClientFilters {
  cups: string[];
  socialReason: string[];
}

const FilterCups = ({
  filters,
  setFilters,
  firstInputChanged,
  setFirstInputChanged,
  masterFiltersCups,
}: FiltersCupsProps) => {
  const { t } = useTranslation();
  const [optionsFilters, setOptionsFilters] = useState<ICupsClientFilters>({
    cups: [],
    socialReason: [],
  });
  const [hasCupsChanged, setHasCupsChanged] = useState<boolean>(true);
  const cupsOptions = useMemo(
    () => [...new Set(masterFiltersCups.map(({ codeCups }) => codeCups))],
    [masterFiltersCups]
  );
  const socialReasonOptions = useMemo(
    () => [
      ...new Set(
        masterFiltersCups
          .map(({ socialReason }) => socialReason)
          .filter((socialReason) => socialReason)
      ),
    ],
    [masterFiltersCups]
  );

  const checkFistInputToChange = useCallback(
    (cups: string[], socialReason: string[]) => {
      if (cups.length && !socialReason.length) {
        setFirstInputChanged("cups");
        return "cups";
      }
      if (socialReason.length && !cups.length) {
        setFirstInputChanged("socialReason");
        return "socialReason";
      }
    },
    [setFirstInputChanged]
  );

  const filterOptions = (
    masterFiltersCups: MasterFiltersManagementCups[],
    id: (keyof MasterFiltersManagementCups)[],
    returnValue: keyof MasterFiltersManagementCups,
    selectedItems: string[][]
  ): string[] =>
    masterFiltersCups
      .filter((cup) =>
        selectedItems.every((items, index) =>
          items.length ? items.some((item) => cup[id[index]] === item) : true
        )
      )
      .filter((cup) => cup[returnValue])
      .map((cup) => cup[returnValue]);

  useEffect(() => {
    if (hasCupsChanged && masterFiltersCups.length) {
      const { cups, socialReason } = filters;
      const firstInputToChange = checkFistInputToChange(cups, socialReason);

      const ifCupsFirstInputChanged =
        firstInputToChange === "cups" || firstInputChanged === "cups";
      const ifSocialReasonFirstInputChanged =
        firstInputToChange === "socialReason" ||
        firstInputChanged === "socialReason";

      const filteredCups = filterOptions(
        masterFiltersCups,
        ["socialReason"],
        "codeCups",
        [socialReason]
      );

      const filteredSocialReason = filterOptions(
        masterFiltersCups,
        ["codeCups"],
        "socialReason",
        [cups]
      );

      setOptionsFilters({
        cups: ifSocialReasonFirstInputChanged
          ? [...new Set(filteredCups)]
          : cupsOptions,
        socialReason: ifCupsFirstInputChanged
          ? [...new Set(filteredSocialReason)]
          : socialReasonOptions,
      });

      setHasCupsChanged(false);
    }
  }, [
    checkFistInputToChange,
    cupsOptions,
    socialReasonOptions,
    filters,
    firstInputChanged,
    hasCupsChanged,
    masterFiltersCups,
  ]);

  const autocompleteFiltersData: AutocompleteCupsFilters[] = [
    {
      id: "cups",
      label: "CUPS",
      placeholder: "Todos los CUPS",
      limitTags: 1,
    },
    {
      id: "socialReason",
      label: "Razón social",
      placeholder: "Todas las razones sociales",
      limitTags: 1,
    },
  ];

  return (
    <Stack className="filters-cups" flexDirection="column">
      {autocompleteFiltersData.map(({ id, label, placeholder, limitTags }) => {
        const isDisabled = !!!optionsFilters[id].length;

        return (
          <FormControl key={id} fullWidth>
            <FormLabel
              sx={{
                fontWeight: "600",
                paddingBottom: ".8rem",
                color: isDisabled ? "#A7B8BF" : "",
              }}
            >
              {t(`Tablas.${label}`)}
            </FormLabel>
            <Autocomplete
              multiple
              disableCloseOnSelect
              disabled={isDisabled}
              options={optionsFilters[id]}
              value={filters[id]}
              noOptionsText={t("Tablas.No hay coincidencias")}
              onChange={(event, value) => {
                setHasCupsChanged(true);
                setFilters((prevDrawerFilters) => {
                  return {
                    ...prevDrawerFilters,
                    [id]: value,
                  };
                });
              }}
              renderOption={(props, option, { selected }) => {
                const { key, ...otherProps } = props;
                return (
                  <ListItem key={key} {...otherProps}>
                    <Checkbox
                      icon={<span className="t-icon-checkbox-default" />}
                      checkedIcon={
                        <span className="t-icon t-icon-checkbox-checked" />
                      }
                      sx={{ fontSize: "24px" }}
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    {option}
                  </ListItem>
                );
              }}
              renderTags={(value, getTagProps) => (
                <>
                  {value.slice(0, limitTags).map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={index}
                      label={
                        <div
                          style={
                            id === "socialReason"
                              ? {
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  maxWidth: 200,
                                }
                              : undefined
                          }
                        >
                          {option}
                        </div>
                      }
                    />
                  ))}
                  {value.length > limitTags && ` +${value.length - limitTags}`}
                </>
              )}
              style={{
                width: "100%",
              }}
              renderInput={(params) => (
                <TextField
                  sx={{
                    width: "100%",
                  }}
                  inputProps={{
                    ...params.inputProps,
                    style: {
                      boxShadow: "none",
                    },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    fullWidth: true,
                    endAdornment: (
                      <Typography
                        component="span"
                        className="t-custom-dropdown t-icon-arrow-down"
                        data-testid="dropdown-arrow"
                        sx={{
                          color: isDisabled ? "#A7B8BF" : "primary.main",
                          position: "absolute",
                          right: "12px",
                          pointerEvents: "none",
                        }}
                      />
                    ),
                  }}
                  placeholder={t(
                    `Tablas.${filters[id].length ? label : placeholder}`
                  )}
                />
              )}
            />
          </FormControl>
        );
      })}
    </Stack>
  );
};

export default FilterCups;
